import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Shield, Clock, Award, Wrench, Droplets, HardHat, ArrowRight, CheckCircle, Thermometer, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import ServiceCard from '../components/ServiceCard';
import FAQ from '../components/FAQ';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/qskuuchf_FB_IMG_1771192477688.jpg';

const services = [
  {
    title: 'Plumbing Repairs & Installations',
    description: 'Leaky pipes, faucets, bathroom/kitchen installations – fast and professional service.',
    icon: Wrench,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/sgdjekh6_file_0000000021b871fdbb75717dd70f8b75%20%281%29.png',
    link: '/services/plumbing-repairs',
  },
  {
    title: 'Electric Boiler Services',
    description: 'Electric boiler installation, repair, and maintenance for optimal home comfort.',
    icon: Thermometer,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/s9lq11vn_FB_IMG_1771192466965.jpg',
    link: '/services/electric-boiler',
  },
  {
    title: 'Water Filtration',
    description: 'Install & service water filtration systems for clean, safe drinking water.',
    icon: Droplets,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/6kwztpbl_file_00000000cc5471fda3a539ab5c1f652c.png',
    link: '/services/water-filtration',
  },
  {
    title: 'New Construction Plumbing',
    description: 'Full plumbing installations for new homes – inspections and compliance included.',
    icon: HardHat,
    image: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/tmjl6pvt_FB_IMG_1771192485955.jpg',
    link: '/services/new-construction',
  },
];

const whyChooseUs = [
  {
    icon: Shield,
    title: 'Licensed & Insured',
    description: 'All work performed by licensed journeymen plumbers with full insurance coverage.',
  },
  {
    icon: Clock,
    title: 'Fast Response',
    description: 'Quick, reliable service across Yarmouth, Digby and Shelburne Counties when you need it most.',
  },
  {
    icon: Award,
    title: 'Emergency Service',
    description: '24/7 emergency plumbing service available for urgent situations.',
  },
];

const reviewImages = [
  {
    url: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/xyw2pu09_Screenshot_20260215_193948_Chrome.jpg',
    alt: 'Google Review - Best Plumbing Service in the Tri-County area'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/rhsjoru3_Screenshot_20260215_194202_Chrome.jpg',
    alt: 'Google Review - Great service, less than half the price'
  },
];

export const Home = () => {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Duncan's Plumbing team and trucks"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#005B96]/95 via-[#005B96]/80 to-[#005B96]/60" />
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-32">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm font-semibold mb-6 animate-fade-in-up">
              Yarmouth's Trusted Plumbing Experts
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 animate-fade-in-up animate-delay-100">
              Trusted Plumbing Company in<br />
              <span className="text-[#FFD700]">Yarmouth, NS</span>
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed animate-fade-in-up animate-delay-200">
              Plumbing, Heating & Water Filtration – Fast, Professional, Licensed service for your home or business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-300">
              <a
                href="tel:902-746-0554"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#005B96] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFD700] hover:text-black transition-all shadow-xl hover:shadow-2xl active:scale-95"
                data-testid="hero-cta-call"
              >
                <Phone size={20} />
                <span>Schedule Service – 902-746-0554</span>
              </a>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#005B96] transition-all"
                data-testid="hero-cta-services"
              >
                <span>View Services</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Services */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
              Our Services
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Plumbing & Heating Services
            </h2>
            <p className="text-gray-600 text-lg">
              From emergency repairs to new installations, we've got all your plumbing and heating needs covered.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service) => (
              <ServiceCard key={service.title} {...service} />
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[#005B96] font-bold text-lg hover:gap-4 transition-all"
              data-testid="view-all-services"
            >
              <span>View All Services</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="reviews-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
              Customer Reviews
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={28} className="fill-[#FFD700] text-[#FFD700]" />
              ))}
            </div>
            <p className="text-gray-600 text-lg">
              We're proud to have earned 5-star reviews from our valued customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {reviewImages.map((review, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow border border-slate-100"
                data-testid={`review-${index}`}
              >
                <img
                  src={review.url}
                  alt={review.alt}
                  className="w-full h-auto"
                />
              </div>
            ))}
          </div>

          {/* QR Code Section */}
          <div className="mt-16 bg-[#E6F2FA] rounded-3xl p-8 md:p-12">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-center lg:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  Leave Us a Review
                </h3>
                <p className="text-gray-600 text-lg leading-relaxed mb-6">
                  Your feedback means a lot to us. If we've recently completed work for you, please scan the QR code to share your experience on Google. Your review helps other customers choose a trusted local plumber and supports our business.
                </p>
                <div className="flex items-center justify-center lg:justify-start gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={24} className="fill-[#FFD700] text-[#FFD700]" />
                  ))}
                </div>
                <p className="text-gray-500">Thank you for your support!</p>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-6 rounded-2xl shadow-xl">
                  <img
                    src="https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/asshrkoh_Screenshot_20260215_193406_Gmail.jpg"
                    alt="Scan to leave a Google Review"
                    className="w-48 h-48 md:w-56 md:h-56 object-contain"
                    data-testid="qr-code"
                  />
                  <p className="text-center text-gray-600 mt-4 font-medium">
                    Scan to leave a Google Review
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-choose-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Your Local Plumbing & Heating Experts
              </h2>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We are a locally owned plumbing and heating company in Yarmouth NS, serving homeowners and businesses with reliable, professional service.
              </p>

              <div className="space-y-6">
                {whyChooseUs.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#E6F2FA] rounded-xl flex items-center justify-center">
                      <item.icon size={24} className="text-[#005B96]" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <a
                href="tel:902-746-0554"
                className="inline-flex items-center gap-2 bg-[#005B96] text-white px-8 py-4 rounded-full font-bold mt-10 hover:bg-[#004a7a] transition-all shadow-lg hover:shadow-xl active:scale-95"
                data-testid="why-choose-cta"
              >
                <Phone size={20} />
                <span>Schedule Service Today</span>
              </a>
            </div>

            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_plumbing-heating-ns/artifacts/qskuuchf_FB_IMG_1771192477688.jpg"
                alt="Duncan's Plumbing team and trucks"
                className="rounded-2xl shadow-2xl w-full"
              />
              {/* Stats Card */}
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#005B96] rounded-full flex items-center justify-center">
                    <CheckCircle size={28} className="text-white" />
                  </div>
                  <div>
                    <p className="font-black text-2xl text-[#005B96]">100%</p>
                    <p className="text-gray-600 text-sm">Licensed & Insured</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="home-faq-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#005B96] font-semibold uppercase tracking-widest text-sm mb-4 block">
                FAQ
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 text-lg">
                Find answers to common questions about our plumbing and heating services.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              <FAQ />
            </div>

            <div className="text-center mt-10">
              <a
                href="tel:902-746-0554"
                className="inline-flex items-center gap-2 bg-[#005B96] text-white px-8 py-4 rounded-full font-bold hover:bg-[#004a7a] transition-all shadow-lg hover:shadow-xl active:scale-95"
                data-testid="faq-cta"
              >
                <Phone size={20} />
                <span>Schedule Service – 902-746-0554</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005B96]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Need a Plumber in Yarmouth?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
            Don't wait – our licensed plumbers are ready to help with all your plumbing and water filtration needs.
          </p>
          <a
            href="tel:902-746-0554"
            className="inline-flex items-center gap-2 bg-white text-[#005B96] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFD700] hover:text-black transition-all shadow-xl hover:shadow-2xl active:scale-95 pulse-glow"
            data-testid="bottom-cta"
          >
            <Phone size={24} />
            <span>Call Now – 902-746-0554</span>
          </a>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="sticky-cta md:hidden">
        <a
          href="tel:902-746-0554"
          className="flex items-center justify-center gap-2 bg-[#005B96] text-white w-full py-4 rounded-full font-bold"
          data-testid="sticky-mobile-cta"
        >
          <Phone size={20} />
          <span>Call Now – 902-746-0554</span>
        </a>
      </div>
    </div>
  );
};

export default Home;
